import { Stack, useTheme } from "@mui/material";
import { Text } from "@src/appV2/redesign/system/ui-components";
import { useMemo } from "react";
import { Virtuoso } from "react-virtuoso";

import { ListLoadingState } from "../../../components/ListLoadingState";
import { type FacilityWithDistance } from "../../data/schemas";
import { EarlyAccessFacilityListItem } from "./EarlyAccessFacilityListItem";

interface FacilityGroup {
  facilities: FacilityWithDistance[];
  totalCount: number;
}

interface EarlyAccessFacilitiesListProps {
  isLoading: boolean;
  isEmpty: boolean;
  searchTerm: string;
  nearMeGroup: FacilityGroup;
  allOtherGroup: FacilityGroup;
  onFacilitySelect: (facility: FacilityWithDistance) => void;
}

export function EarlyAccessFacilitiesList(props: EarlyAccessFacilitiesListProps) {
  const { isLoading, isEmpty, searchTerm, nearMeGroup, allOtherGroup, onFacilitySelect } = props;

  const theme = useTheme();

  // Create sorted array: near me facilities first, then others
  const sortedFacilities = useMemo(
    () => [...nearMeGroup.facilities, ...allOtherGroup.facilities],
    [nearMeGroup.facilities, allOtherGroup.facilities]
  );

  if (isLoading) {
    return (
      <Stack
        sx={{
          flex: 1,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <ListLoadingState
          texts={[
            "Finding facilities nearby",
            "Loading facility information",
            "Facilities are on the way",
          ]}
        />
      </Stack>
    );
  }

  if (isEmpty) {
    return (
      <Stack
        sx={{
          flex: 1,
          justifyContent: "center",
          alignItems: "center",
          padding: 6,
        }}
      >
        <Text variant="body1" color={(theme) => theme.palette.text.secondary}>
          {searchTerm ? `No facilities found matching "${searchTerm}"` : "No facilities available"}
        </Text>
      </Stack>
    );
  }

  return (
    <Virtuoso
      data={sortedFacilities}
      itemContent={(index, facility) => (
        <EarlyAccessFacilityListItem
          facility={facility}
          showDividerAfter={index < sortedFacilities.length - 1}
          onClick={() => {
            onFacilitySelect(facility);
          }}
        />
      )}
      style={{
        backgroundColor: theme.palette.common.white,
      }}
      data-testid="early-access-facilities-list-scroll-container"
    />
  );
}
